import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {MultiSelect} from 'primereact/multiselect'
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useQueryParams} from '../../utils/NavigatorUtils'
import {useAuth} from '../authentication/AuthProvider'
import DialogWrapper from '../new-table/DialogWrapper'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {useListStaticList} from '../static-list/query-keys/StaticListKeysManager'
import {useDatatableContext} from './DataTableContext'
import {
    filterEventTypes,
    filterExplainEvent,
    filterExplainEventTypes,
    getFilterExplainEventKey,
    onFilterChangeListener,
    onFilterExplainChangeListener,
    removeFilterModeChangeListener,
} from './broadcast/FilterEvent'

const StyledFilterByStaticList = forwardRef(({onFilter = () => {}}, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const {translateDatatable, translateCommons, translateOptions} =
        TranslateUtils()

    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {tableType} = useDatatableContext()

    //#region URL Query Params
    const query = useQueryParams()
    const queryParamsAdvancedFilter = useMemo(() => {
        const selectedStaticListIds = query?.get('staticListId')
            ? [parseInt(query?.get('staticListId'))]
            : []

        return selectedStaticListIds
    }, [query])
    //#endregion

    const panelVisible = useSignal(false)
    const selectedItems = useSignal(
        queryParamsAdvancedFilter ? queryParamsAdvancedFilter : [],
    )
    const filterByStaticListRef = useRef(null)

    const {data: staticListOptions} = useListStaticList(
        user?.id,
        true,
        {
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            ownerId: user?.id,
        },
        !!user?.id,
    )

    useEffect(() => {
        if (
            isArrayAndNotEmpty(staticListOptions) &&
            isArrayAndNotEmpty(queryParamsAdvancedFilter)
        ) {
            onFilterChangeListener(tableType, filterEventTypes.staticList, {
                staticListIds: queryParamsAdvancedFilter,
            })

            let selectedStaticLists = []

            selectedStaticLists = staticListOptions?.filter(item =>
                queryParamsAdvancedFilter.includes(item.value),
            )

            onFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.STATIC_LIST_FILTER_EXPLAIN_CHANGE,
                selectedStaticLists,
            )
        }
    }, [staticListOptions, queryParamsAdvancedFilter])

    const handleShowForm = () => {
        blockOverflownScroll()
        filterByStaticListRef.current.onShow()
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        filterByStaticListRef.current.onHide()
    }

    const handleChangeSelectedItems = e => {
        selectedItems.value = e.value === null ? [] : e.value
    }

    const handleSubmit = () => {
        onFilterChangeListener(tableType, filterEventTypes.staticList, {
            staticListIds: selectedItems.value,
        })

        let filteredSelectedItems = []

        if (isArrayAndNotEmpty(selectedItems.value)) {
            filteredSelectedItems = staticListOptions?.filter(item =>
                selectedItems.value.includes(item.value),
            )
        }

        onFilterExplainChangeListener(
            tableType,
            filterExplainEventTypes.STATIC_LIST_FILTER_EXPLAIN_CHANGE,
            filteredSelectedItems,
        )

        handleHideForm()
    }

    const panelVisibilityHandler = () => ({
        optionLabel: 'label',
        optionValue: 'value',
        emptyFilterMessage: translateCommons('No results found'),
        resetFilterOnHide: true,
        filter: true,
        filterBy: 'label',
        filterPlaceholder: translateCommons('Search here'),
        showClear: true,
        onShow: () => (panelVisible.value = true),
        onHide: () => (panelVisible.value = false),
    })

    const panelVisiblilityProps = panelVisibilityHandler()

    const renderPanelFooterTemplate = selectedData => {
        let shouldRenderItemSelected = false
        let itemSelectedLength = 0

        if (!!selectedData && typeof selectedData === 'object') {
            if (isArrayAndNotEmpty(selectedData)) {
                shouldRenderItemSelected = true

                itemSelectedLength = selectedData?.length
            }

            if (
                !shouldRenderItemSelected &&
                Object.keys(selectedData)?.length > 0
            ) {
                shouldRenderItemSelected = true

                itemSelectedLength = Object.keys(selectedData)?.length
            }
        }

        return (
            <div className="p-3" style={{backgroundColor: '#f9f9f9'}}>
                <b>
                    {shouldRenderItemSelected && itemSelectedLength > 0
                        ? `${itemSelectedLength} ${
                              itemSelectedLength > 1 ? 'items' : 'item'
                          } selected`
                        : `No item selected`}
                </b>
            </div>
        )
    }

    useEffect(() => {
        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )}`,
            () => {
                selectedItems.value = []
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_STATIC_LIST_FILTER,
            )}`,
            () => {
                selectedItems.value = []
            },
        )

        return () => {
            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )

            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_STATIC_LIST_FILTER,
            )
        }
    }, [])

    return (
        <DialogWrapper
            header={translateDatatable('Filter by static list')}
            ref={filterByStaticListRef}
            onSubmit={handleSubmit}>
            <div className="flex flex-column gap-2">
                <p className="m-0 p-0 mb-3">
                    {translateDatatable(
                        'Select multiple static lists to display the patient list according to the chosen static lists',
                    )}
                </p>

                <MultiSelect
                    className="w-full h-3rem p-inputtext p-0 font-bold"
                    options={
                        isArrayAndNotEmpty(staticListOptions)
                            ? translateOptions(staticListOptions)
                            : []
                    }
                    placeholder={translateCommons('Select an option')}
                    defaultValue={[]}
                    maxSelectedLabels={3}
                    display={selectedItems.value?.length < 4 ? 'chip' : 'comma'}
                    value={selectedItems.value || []}
                    onChange={handleChangeSelectedItems}
                    itemTemplate={item => {
                        return (
                            <h6 className="mb-0 font-bold">
                                <span className="mr-2">
                                    <i className="pi pi-list"></i>
                                </span>
                                <span className="text-darkcyan capitalized">
                                    {item?.label}
                                </span>
                                {item?.extraData
                                    ? ` - Available ${item?.extraData} patient(s)`
                                    : ' - No patient added yet'}
                            </h6>
                        )
                    }}
                    panelFooterTemplate={() =>
                        renderPanelFooterTemplate(selectedItems.value)
                    }
                    {...panelVisiblilityProps}
                />

                <p className="font-bold">
                    You have selected{' '}
                    <span className="text-darkcyan">
                        {selectedItems.value?.length < 1
                            ? 'no'
                            : selectedItems.value?.length}{' '}
                        static list(s)
                    </span>{' '}
                    out of{' '}
                    <span className="text-darkcyan">
                        {staticListOptions?.length} available static list(s)
                    </span>
                </p>

                {/* Based on selectedItems.value renders appropriate static list */}
                <div
                    className="flex flex-wrap align-items-center gap-2"
                    style={{
                        overflow: 'auto',
                    }}>
                    {isArrayAndNotEmpty(selectedItems.value) &&
                        staticListOptions?.map(staticList => {
                            if (
                                selectedItems.value?.includes(staticList?.value)
                            ) {
                                return (
                                    <div
                                        key={staticList?.value}
                                        className="p-2  bg-white p-1 border-round-lg border-lightgreen mr-2">
                                        <h6 className="mb-0 font-bold px-2">
                                            <span className="mr-2">
                                                <i className="pi pi-list"></i>
                                            </span>
                                            <span className="text-darkcyan capitalized">
                                                {staticList?.label}
                                            </span>
                                            {staticList?.extraData
                                                ? ` - Available ${staticList?.extraData} patient(s)`
                                                : ' - No patient added yet'}
                                        </h6>
                                    </div>
                                )
                            }
                        })}
                </div>
            </div>
        </DialogWrapper>
    )
})

export default StyledFilterByStaticList
